import React from "react";
import { Box, Container, Paper, Stack, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import Header from "../components/Header/userHeader";
import { UserFooter } from "../components";

const sections = [
  {
    title: "1. Information We Collect",
    bullets: [
      [
        "Account Information",
        "Name, phone number, email address, and profile credentials.",
      ],
      [
        "Location Data",
        "Precise or approximate device location data (via GPS/IP, with your permission) is required while the app is running to show available local merchants, calculate accurate ETAs, dispatch service providers, and prevent fraud.",
      ],
      [
        "Fulfillment Data",
        "Details related to orders, items purchased, service categories selected, and delivery addresses.",
      ],
      [
        "Device Data",
        "Device model, OS, unique identifiers, and app interaction analytics.",
      ],
    ],
  },
  {
    title: "2. Payment Data and Financial Security",
    blocks: [
      "All digital payments on Sthanick are securely processed through our authorized payment gateway, Razorpay. Sthanick does not collect, store, or process sensitive financial data (e.g., full credit/debit card numbers, UPI PINs, or net banking passwords). For COD and off-platform service settlements, Sthanick does not collect or process financial transaction data.",
    ],
  },
  {
    title: "3. How We Use Your Information",
    blocks: [
      "We utilize collected data strictly to process orders, facilitate logistics, match users with Service Providers, send updates, prevent fraud, and optimize platform routing algorithms.",
    ],
  },
  {
    title: "4. How We Share Your Information & Disclosure",
    blocks: [
      "As an intermediary network, we do not sell your personal data. We only share specific information with independent third parties to fulfill requests and operate the platform. By using the platform, you consent to sharing data with:",
    ],
    bullets: [
      [
        "Category A (Sthanick-Fulfilled Orders)",
        "We share order details with the Merchant. We share your full name, phone number, and delivery address with independent Delivery Partners.",
      ],
      [
        "Category B (Vendor Self-Delivery Orders)",
        "We share your full name, phone number, and delivery address directly with the Merchant so their internal staff can deliver your order.",
      ],
      [
        "Category C (Hyperlocal Services)",
        "We share your contact details, location, and requested service scope with the independent Service Provider so they can navigate to your location.",
      ],
      [
        "Trusted Third Parties",
        "SMS providers, payment gateways (like Razorpay), and logistics partners.",
      ],
      [
        "Affiliates, Partners, & Successors",
        "Shared strictly under lawful terms, or with successors in the event of a business restructuring or sale.",
      ],
      [
        "Legal Authorities",
        "We may disclose data if mandated by Indian law or public authorities.",
      ],
    ],
  },
  {
    title: "5. Data Security & Storage",
    blocks: [
      "We store your data securely on cloud infrastructure (e.g., AWS). We follow industry-standard practices to protect your data against unauthorized access, disclosure, or alteration.",
    ],
  },
  {
    title: "6. Data Retention",
    blocks: [
      "Personal data is retained as long as your account remains active, or as necessary to provide our services and comply with legal obligations. Upon request, users can ask for data deletion.",
    ],
  },
  {
    title: "7. Your Privacy Rights",
    blocks: [
      "You have the right to access, request a copy of, correct, or request the deletion of your personal data, subject to applicable legal requirements. You can update profile data in-app or revoke location permissions via device settings (though this will limit hyperlocal functionality).",
    ],
  },
  {
    title: "8. Children's Privacy",
    blocks: [
      "Our services are not directed at children under the age of 13. We do not knowingly collect data from children. If we learn we have collected such data, it will be deleted immediately.",
    ],
  },
  {
    title: "9. Third-Party Links",
    blocks: [
      "Our platform may contain links to third-party sites. We are not responsible for their privacy practices and encourage you to read their respective privacy policies.",
    ],
  },
  {
    title: "10. Grievance Officer",
    blocks: [
      "In accordance with the Information Technology Act, 2000 and rules made thereunder, contact our Grievance Officer at:",
    ],
    contact: {
      company: "Arthavyavas Private Limited",
      address: "Raidurgam, Hyderabad, Telangana",
      email: "Support@sthanick.com",
      time: "Monday to Friday (9:00 AM to 6:00 PM)",
    },
  },
  {
    title: "11. Updates to Policy",
    blocks: [
      "This policy may change from time to time. We will notify users of significant changes.",
    ],
    smallText: "Last updated: April 10, 2026.",
  },
  {
    title: "12. Governing Law",
    blocks: [
      "This policy is governed by Indian law, with exclusive jurisdiction in Hyderabad, Telangana.",
    ],
  },
];

const Privacypolicy = () => {
  return (
    <Box
      sx={{
        bgcolor: "#fbf5ec",
        minHeight: "100vh",
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Header />
      <Box
        component="section"
        sx={{
          bgcolor: "#ff6b0b",
          color: "#fff",
          textAlign: "center",
          pt: { xs: 7, md: 6.8 },
          pb: { xs: 7, md: 7.8 },
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 38, md: 50 },
              lineHeight: 1.1,
              fontWeight: 900,
              letterSpacing: 0,
              mb: 2,
            }}
          >
            Privacy Policy
          </Typography>
          <Typography
            sx={{
              maxWidth: 620,
              mx: "auto",
              fontSize: { xs: 17, md: 19 },
              lineHeight: 1.45,
              letterSpacing: 0.6,
              color: "rgba(255,255,255,0.92)",
            }}
          >
            We are deeply committed to protecting your privacy and securing your
            personal information.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth={false} sx={{ maxWidth: 880, px: { xs: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            mt: 5,
            mb: 5,
            borderRadius: "12px 12px 10px 10px",
            borderTop: "2px solid #f05a1a",
            bgcolor: "#fff",
            boxShadow: "0 16px 38px rgba(51, 36, 22, 0.12)",
            px: { xs: 3, sm: 4, md: 5 },
            py: { xs: 4, md: 4.8 },
          }}
        >
          <Typography sx={{ ...paragraphSx, mb: 4.6 }}>
            Arthavyavas Private Limited ("Company", "we", "us", or "our") is
            deeply committed to protecting your privacy and securing your
            personal information while operating the Sthanick platform.
          </Typography>

          {sections.map((section) => (
            <PolicySection key={section.title} section={section} />
          ))}
        </Paper>
      </Container>

      {/* <Box
        component="footer"
        sx={{
          bgcolor: "#070c15",
          color: "#fff",
          textAlign: "center",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          py: 3.4,
        }}
      >
        <Stack alignItems="center" spacing={1.4}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.8}>
            <LocationOnOutlinedIcon sx={{ color: "#ff6b1a", fontSize: 24 }} />
            <Typography sx={{ fontSize: 23, fontWeight: 900 }}>Sthanick</Typography>
          </Stack>
          <Typography sx={{ color: "#8791a4", fontSize: 14 }}>
            An initiative by Arthavyavas Private Limited
          </Typography>
          <Typography sx={{ color: "#626d81", fontSize: 13 }}>
            {"\u00a9 2026 Sthanick. All rights reserved."}
          </Typography>
        </Stack>
      </Box> */}
      <UserFooter />
    </Box>
  );
};

const paragraphSx = {
  color: "#314054",
  fontSize: { xs: 16, md: 17 },
  lineHeight: 1.58,
  letterSpacing: 0.45,
  mb: 1.9,
};

const headingSx = {
  color: "#f05a1a",
  fontSize: { xs: 22, md: 25 },
  fontWeight: 900,
  letterSpacing: 0.5,
  borderBottom: "1px solid #f0dfcf",
  pb: 1,
  mb: 2.3,
};

const PolicySection = ({ section }) => (
  <Box component="section" sx={{ mb: { xs: 4.1, md: 4.7 } }}>
    <Typography component="h2" sx={headingSx}>
      {section.title}
    </Typography>

    {section.blocks?.map((text) => (
      <Typography key={text} sx={paragraphSx}>
        {text}
      </Typography>
    ))}

    {section.bullets && (
      <Box
        component="ul"
        sx={{
          m: 0,
          pl: 2.4,
          mb: 2.8,
          "& li::marker": { color: "#f05a1a" },
        }}
      >
        {section.bullets.map(([label, text]) => (
          <Typography
            component="li"
            key={label}
            sx={{ ...paragraphSx, display: "list-item", mb: 1.5, pl: 0.4 }}
          >
            <Box component="strong" sx={{ color: "#071326", fontWeight: 900 }}>
              {label}:
            </Box>{" "}
            {text}
          </Typography>
        ))}
      </Box>
    )}

    {section.contact && (
      <Box
        sx={{
          border: "1px solid #f5ddc0",
          bgcolor: "#fff8ef",
          borderRadius: 1,
          color: "#314054",
          fontSize: { xs: 14, md: 15 },
          lineHeight: 1.65,
          letterSpacing: 0.35,
          p: { xs: 2, md: 2.2 },
          mb: 2.5,
        }}
      >
        <Typography
          sx={{ color: "#071326", fontSize: 14, fontWeight: 900, mb: 0.5 }}
        >
          {section.contact.company}
        </Typography>
        <Typography sx={{ fontSize: 14 }}>{section.contact.address}</Typography>
        <Typography sx={{ fontSize: 14 }}>
          <Box component="strong" sx={{ color: "#071326", fontWeight: 900 }}>
            Email:
          </Box>{" "}
          <Box
            component="span"
            sx={{ color: "#f05a1a", textDecoration: "underline" }}
          >
            {section.contact.email}
          </Box>
        </Typography>
        <Typography sx={{ fontSize: 14 }}>
          <Box component="strong" sx={{ color: "#071326", fontWeight: 900 }}>
            Time:
          </Box>{" "}
          {section.contact.time}
        </Typography>
      </Box>
    )}

    {section.smallText && (
      <Typography
        sx={{ color: "#314054", fontSize: 12, fontWeight: 700, mt: -0.8 }}
      >
        {section.smallText}
      </Typography>
    )}
  </Box>
);

export default Privacypolicy;
